// Category landing pages (/women-safety, /kids-safety, /elder-safety,
// /family-safety) — single data source driving BOTH the React component
// (CategoryPage.jsx) and the build-time body prerender (scripts/
// prerender-meta.mjs), so raw HTML and client render can never drift.
// Pure JS only — no JSX, no React imports.
//
// Copy rules: only claims confirmed elsewhere on the site (plans, Hermes
// deviation detection, SOS escalation, safe zones, location history,
// wearable as a planned add-on). No AI/pattern-learning claims on the kids
// page. No fall-detection / dementia-wandering claims until those exist.

export const CATEGORY_PAGES = [
  {
    path: "/women-safety",
    eyebrow: "For women",
    h1: "NISCHINT — Women Safety App with AI Deviation Detection",
    metaTitle: "Women Safety App India | AI Alerts & SOS — NISCHINT",
    metaDescription:
      "NISCHINT is a women's safety app for India with live location sharing, an SOS you hold for 3 seconds to alert your circle, and Hermes AI that detects unusual deviations — not constant tracking.",
    intro:
      "Looking for a women safety app in India that doesn't mean someone watching your every move? NISCHINT is a women safety app with live location sharing that stays silent on a normal day — and a panic button app for women the moment it isn't. Hermes, our AI, learns your normal patterns and flags genuine deviations, so help reaches you without surveillance.",
    bullets: [
      "Live location sharing with Hermes AI deviation detection — notices when a journey goes off-pattern, not a passive tracking feed",
      "An SOS you hold for 3 seconds, with 4-level escalation — from a gentle check-in to your full trusted circle",
      "A safe commute app for women — built for daily travel, night journeys, cabs and shared autos across Indian cities",
      "Alerts reach only your chosen trusted contacts, and only when something is actually off",
    ],
    tierCallout:
      "7-day free trial, then Individual ₹299/month or Family ₹999/month. Prices include GST.",
    links: [{ href: "/plans", label: "See plans and pricing" }],
    cta: { label: "Try NISCHINT free — 7-day trial", href: "/#waitlist" },
  },
  {
    path: "/kids-safety",
    eyebrow: "For kids",
    h1: "Child Safety App India — Safe Zones & SOS for Kids",
    metaTitle: "Child Safety App India — Safe Zones & SOS for Kids | NISCHINT",
    metaDescription:
      "Parent-set safe zones for home, school and tuition, and an SOS your child holds for 3 seconds that alerts parents only. Kids features launching soon. Join the waitlist.",
    intro:
      "NISCHINT is a child safety app for India built around parent-set safe zones: choose the places that matter — home, school, tuition — and get an alert when your child leaves or arrives. No constant pings, no watching a dot. And if your child needs help, your child can hold SOS for 3 seconds to alert you. Kids features are launching soon. Join the waitlist to be first in.",
    bullets: [
      "Safe-zone alerts when your child leaves or arrives at home, school or tuition",
      "An SOS your child holds for 3 seconds, which alerts parents only, never strangers or volunteers",
      "Parents get an alert when a safe-zone boundary is crossed — not a stream of constant location pings",
    ],
    tierCallout:
      "Family plan (₹999 a month, GST inclusive) covers up to 4 members. Children are always protected members, never guardians.",
    links: [
      { href: "/family-safety", label: "NISCHINT for the whole family" },
      { href: "/plans", label: "See plans and pricing" },
    ],
    cta: { label: "Join the Kids waitlist", href: "#kids-safety-waitlist" },
    // Embeds the same WaitlistForm as /kids (persona=kids) under the CTA.
    waitlist: {
      id: "kids-safety-waitlist",
      persona: "kids",
      submitLabel: "Join the Kids waitlist",
    },
  },
  {
    path: "/elder-safety",
    eyebrow: "For seniors",
    h1: "Elderly Safety App India — Peace of Mind Without a Leash",
    metaTitle: "Senior Citizen Safety App India | Inactivity Alerts — NISCHINT",
    metaDescription:
      "Monitor elderly parents' safety without invasive tracking. NISCHINT alerts family only when a pattern breaks — for seniors living independently in India.",
    intro:
      "An app to monitor elderly parents shouldn't feel like surveillance. NISCHINT is an elderly safety app for India built independence-first: silent by default while your parent's day follows its normal pattern, and quick to alert family when that pattern breaks — whether you live together or a thousand kilometres apart.",
    bullets: [
      "Independence-first design — our 'For Papa' principle: a safety net, not a leash, with dignity preserved",
      "Silent by default — alerts only when a daily pattern breaks, like a missed morning walk",
      "Works whether family lives together, across the city, or across the world",
      "Optional wearable for continuous reassurance — a planned add-on",
    ],
    tierCallout: "7-day free trial, then Individual ₹299/month. Prices include GST.",
    links: [
      {
        href: "/blog/parent-safe-alone-at-home",
        label: "Is my parent safe alone at home? A decision guide",
      },
      { href: "/plans", label: "See plans and pricing" },
      {
        href: "/for-nri-families",
        label:
          "Managing your parents' safety from abroad? See how NISCHINT works for NRI families",
      },
    ],
    cta: { label: "Try NISCHINT free — 7-day trial", href: "/#waitlist" },
  },
  {
    path: "/family-safety",
    eyebrow: "For families",
    h1: "Family Safety App India — Location Sharing Without Constant Tracking",
    metaTitle: "Family Locator App India | Privacy-First — NISCHINT",
    metaDescription:
      "Share live location with family, get emergency alerts, and stay connected — without the anxiety of 24/7 tracking. NISCHINT's family safety plan covers everyone.",
    intro:
      "Most family locator apps trade peace of mind for the anxiety of always-on tracking. NISCHINT is a family safety app without constant tracking: share live location with family when it matters, get emergency alerts when patterns break, and stay connected the rest of the time without anyone watching a map.",
    bullets: [
      "Family plan (₹999/month) — up to 4 members, each both guardian and protected, one subscription",
      "Location history and the full Alert Center for the whole family",
      "Hermes AI watches for genuine deviations — nobody monitors a live dot on a map",
      "An SOS you hold for 3 seconds, with 4-level escalation — covers every member, kids to grandparents",
    ],
    tierCallout:
      "Family plan: ₹999/month for up to 4 members — starts with a 7-day free trial, prices include GST.",
    links: [
      { href: "/plans", label: "Compare plans and pricing" },
      { href: "/women-safety", label: "For women" },
      { href: "/kids-safety", label: "For kids" },
      { href: "/elder-safety", label: "For seniors" },
    ],
    cta: { label: "Try NISCHINT free — 7-day trial", href: "/#waitlist" },
  },
];

export const CATEGORY_PATHS = CATEGORY_PAGES.map((p) => p.path);

export const getCategoryPage = (path) =>
  CATEGORY_PAGES.find((p) => p.path === path) || null;

// Service schema for category pages (Organization is injected separately by
// the shared SEO layer).
export const makeServiceSchema = (page, siteUrl) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: `NISCHINT — ${page.eyebrow}`,
  serviceType: "AI-powered personal safety application",
  description: page.metaDescription,
  url: `${siteUrl}${page.path}`,
  provider: {
    "@type": "Organization",
    name: "NISCHINT",
    url: siteUrl,
  },
  areaServed: { "@type": "Country", name: "India" },
});

// Visible FAQ copy per category page — drives BOTH the on-page FAQ section
// (CategoryPage.jsx + renderCategoryHtml below) and the FAQPage JSON-LD
// (makeFaqSchema), so markup always matches visible content. Same pattern as
// PLANS_FAQ / SAFETY_TRUST_FAQ in seoData.mjs. Copy rules: only facts already
// published elsewhere on the site (plans page prices, Hermes deviation
// detection, SOS escalation, safe zones, location history, wearable planned).
export const CATEGORY_FAQS = {
  "/women-safety": [
    {
      q: "Does NISCHINT track my location all the time?",
      a: "No. Live location is shared only with your chosen trusted contacts, and Hermes AI alerts them only when a journey goes off-pattern — not on a normal day.",
    },
    {
      q: "What happens when I press the SOS button?",
      a: "Hold the SOS for 3 seconds to start a 4-level escalation — from a gentle check-in to your full trusted circle.",
    },
    {
      q: "Does it work for night travel and cabs?",
      a: "Yes. NISCHINT is built for daily commutes, night journeys, cabs and shared autos across Indian cities.",
    },
    {
      q: "How much does it cost?",
      a: "Every plan starts with a 7-day free trial. Then Individual is ₹299/month and Family is ₹999/month — prices include GST.",
    },
  ],
  "/kids-safety": [
    {
      q: "How do safe zones work?",
      a: "You set safe zones like home, school and tuition, and get an alert when your child leaves or arrives — no constant pings.",
    },
    {
      q: "Can my child call for help themselves?",
      a: "Yes. Your child holds the SOS for 3 seconds, and every parent is alerted with their location. Kids features launch soon.",
    },
    {
      q: "Which plan covers more than one child?",
      a: "The Family plan at ₹999/month covers up to 4 members under one subscription — prices include GST.",
    },
  ],
  "/elder-safety": [
    {
      q: "Will my parent feel watched?",
      a: "No. NISCHINT is silent by default — family is alerted only when a daily pattern breaks, like a missed morning walk. A safety net, not a leash.",
    },
    {
      q: "We live in different cities. Does it still work?",
      a: "Yes. It works whether family lives together, across the city, or across the world.",
    },
    {
      q: "Is there a wearable option?",
      a: "An optional wearable for continuous reassurance is a planned add-on.",
    },
    {
      q: "I'm arranging this from abroad. Where do I start?",
      a: "NISCHINT works across countries — your parent in India, you anywhere. See the NRI families link on this page for how it works when you're coordinating from overseas.",
    },
  ],
  "/family-safety": [
    {
      q: "How is this different from a family locator app?",
      a: "Locator apps keep everyone watching a live map. NISCHINT's Hermes AI watches for genuine deviations and alerts only then — location is shared when it matters, not constantly.",
    },
    {
      q: "How many people does the Family plan cover?",
      a: "Up to 4 members under one ₹999/month subscription — each both guardian and protected.",
    },
    {
      q: "How far back does location history go?",
      a: "The Family plan includes location history and the full Alert Center for the whole family.",
    },
    {
      q: "Does one plan really cover kids and grandparents?",
      a: "Yes. An SOS you hold for 3 seconds, with 4-level escalation, covers every member, kids to grandparents.",
    },
  ],
};

// FAQPage schema for a category page — separate JSON-LD block, injected
// alongside (not inside) Organization and Service.
export const makeFaqSchema = (page) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: (CATEGORY_FAQS[page.path] || []).map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

// Visible FAQ block for the static prerender — mirrors the FAQ section in
// CategoryPage.jsx (keep the two in sync when editing).
const renderFaqHtml = (path) => {
  const faqs = CATEGORY_FAQS[path] || [];
  if (!faqs.length) return "";
  return `
      <section style="margin-top:56px;">
        <h2 class="text-base md:text-lg font-bold text-[#14171A]">Common questions</h2>
        <div class="mt-5 space-y-5">
          ${faqs
            .map(
              (f) => `<div>
            <p class="font-semibold text-[#14171A]">${esc(f.q)}</p>
            <p class="mt-1 text-[#4A453C] leading-relaxed">${esc(f.a)}</p>
          </div>`
            )
            .join("\n          ")}
        </div>
      </section>`;
};

export const esc = (s) =>
  String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;");

const CHECK_SVG =
  '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><circle cx="9" cy="9" r="8" fill="#16C784" fill-opacity="0.15" stroke="#16C784" stroke-width="1.5"/><path d="M5.5 9.2l2.2 2.2 4.8-4.8" stroke="#16C784" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

// Static body HTML for the build-time prerender. Mirrors CategoryPage.jsx's
// structure and classes — keep the two in sync when editing.
export const renderCategoryHtml = (page) => `
      <header style="padding:16px 24px;border-bottom:1px solid #E3DBC9;">
        <a href="/" aria-label="NISCHINT home"><img src="/logo.png" alt="NISCHINT" style="height:56px;width:auto;" /></a>
        <nav style="display:inline-flex;gap:24px;margin-left:32px;">
          <a href="/plans">Plans</a>
          <a href="/about">About</a>
          <a href="/blog">Blog</a>
        </nav>
      </header>
      <main class="max-w-3xl mx-auto px-6 py-16 sm:py-24">
        <p class="text-sm font-semibold text-[#1E88E5] mb-4">${esc(page.eyebrow)}</p>
        <h1 class="text-4xl sm:text-5xl tracking-tight font-extrabold text-[#14171A]">${esc(page.h1)}</h1>
        <p class="mt-6 text-base sm:text-lg leading-relaxed text-[#4A453C]">${esc(page.intro)}</p>
        <ul class="mt-8 space-y-4">
          ${page.bullets
            .map(
              (b) => `<li style="display:flex;gap:12px;align-items:flex-start;">${CHECK_SVG}<span class="text-[#4A453C] leading-relaxed">${esc(b)}</span></li>`
            )
            .join("\n          ")}
        </ul>
        <div class="mt-10 bg-white border border-[#E3DBC9] rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(20,23,26,0.05)]">
          <p class="text-[#4A453C] leading-relaxed">${esc(page.tierCallout)}</p>
          ${page.links
            .map(
              (l) =>
                `<a href="${esc(l.href)}" style="display:inline-block;margin-right:24px;margin-top:12px;font-weight:600;color:#1E88E5;">${esc(l.label)} →</a>`
            )
            .join("\n          ")}
        </div>
        <p style="margin-top:40px;">
          <a href="${esc(page.cta.href)}" style="display:inline-block;background:#16C784;color:#14171A;font-weight:700;padding:16px 32px;border-radius:9999px;text-decoration:none;">${esc(page.cta.label)}</a>
        </p>
        ${page.waitlist ? `
        <div id="${esc(page.waitlist.id)}" style="margin-top:24px;max-width:42rem;">
          <p style="font-size:16px;font-weight:600;color:#14171A;">Kids features are launching soon. Join the waitlist to be first in.</p>
          <p style="margin-top:8px;font-size:14px;color:#4A453C;"><a href="/#waitlist" style="font-weight:600;color:#1E88E5;">Leave your details on the homepage waitlist</a> and we'll tell you the day Kids mode opens.</p>
        </div>` : ""}
        ${renderFaqHtml(page.path)}
      </main>
      <footer style="padding:32px 24px;border-top:1px solid #E3DBC9;color:#726B5E;font-size:14px;">
        © ${new Date().getFullYear()} Skybyte Venture Pvt. Ltd. ·
        <a href="/privacy">Privacy</a> · <a href="/terms">Terms</a> ·
        <a href="/trust-and-safety">Trust &amp; Safety</a>
      </footer>`;
